import api from '@/lib/api/axios'

/**
 * A user's sign-in devices (MFA build plan, Stage 1): one per enrolled second factor.
 * Mirrors backend lib/user-devices.js publicDevice - never any key material.
 */
export type DeviceKind = 'passkey' | 'totp' | 'app_key'
export interface Device {
    id: number
    kind: DeviceKind
    name: string
    makeModel: string | null
    os: string | null
    appVersion: string | null
    aaguid: string | null
    enrolledAt: string | null
    /** How it came to be enrolled: migrated | password | existing_factor | enrolment_code */
    enrolledVia: string
    enrolledBy: number | null
    lastUsedAt: string | null
    lastUsedIp: string | null
    status: 'active' | 'revoked'
    revokedAt: string | null
    revokedBy: number | null
    revokeReason: string | null
}

interface Wrapped<T> { success: boolean; data: T }
/** A request made on the step-up token, not the session (lib/api/axios.ts). */
const steppedUp = (stepUpToken: string) => ({ stepUpToken }) as Record<string, unknown>

export const devicesApi = {
    // ── your own ────────────────────────────────────────────────────────────────────────────────
    list: async () => (await api.get<Wrapped<{ devices: Device[] }>>('/api/me/devices')).data.data.devices,

    /** Prove a device you already have. Each answers with a five-minute step-up token. */
    stepUpTotp: async (code: string) =>
        (await api.post<Wrapped<{ stepUpToken: string; expiresInSeconds: number }>>('/auth/step-up/totp', { code })).data.data,
    stepUpPasskeyBegin: async () => (await api.post<Wrapped<any>>('/auth/step-up/passkey/begin')).data.data,
    stepUpPasskeyComplete: async (credential: unknown) =>
        (await api.post<Wrapped<{ stepUpToken: string; expiresInSeconds: number }>>('/auth/step-up/passkey/complete', { credential })).data.data,

    totpBegin: async (token: string) =>
        (await api.post<Wrapped<{ secret: string; qrCode: string; setupTicket: string }>>('/api/me/devices/totp/begin', {}, steppedUp(token))).data.data,
    totpConfirm: async (token: string, body: { code: string; setupTicket: string; name?: string }) =>
        (await api.post<Wrapped<{ device: Device }>>('/api/me/devices/totp/confirm', body, steppedUp(token))).data.data.device,
    passkeyBegin: async (token: string) => (await api.post<Wrapped<any>>('/api/me/devices/passkey/begin', {}, steppedUp(token))).data.data,
    passkeyComplete: async (token: string, body: { credential: unknown; name?: string }) =>
        (await api.post<Wrapped<{ device: Device }>>('/api/me/devices/passkey/complete', body, steppedUp(token))).data.data.device,
    remove: async (token: string, id: number) =>
        (await api.delete<Wrapped<{ device: Device }>>(`/api/me/devices/${id}`, steppedUp(token))).data.data.device,

    // ── an administrator, for a user ────────────────────────────────────────────────────────────
    listForUser: async (userId: number) =>
        (await api.get<Wrapped<{ user: { id: number; email: string; name: string }; devices: Device[] }>>(`/api/users/${userId}/devices`)).data.data,
    revokeForUser: async (userId: number, deviceId: number, reason: string) =>
        (await api.post<Wrapped<{ device: Device }>>(`/api/users/${userId}/devices/${deviceId}/revoke`, { reason })).data.data.device,
}

export const deviceKeys = {
    mine: ['devices', 'mine'] as const,
    forUser: (userId: number) => ['devices', 'user', userId] as const,
}
